import React, { KeyboardEventHandler, forwardRef } from 'react';
import classNames from 'classnames';

import { Comp, Number, Text, useId, useTheme } from '@redsift/design-system';

import { LegendItemProps } from './types';
import { StyledLegendItem } from './styles';
import { monochrome } from '../../scheme';
import { LegendVariant } from '../../types';

const COMPONENT_NAME = 'LegendItem';
const CLASSNAME = 'redsift-legend-item';

export const LegendItem: Comp<LegendItemProps, HTMLLIElement> = forwardRef((props, ref) => {
  const {
    className,
    color = monochrome,
    data,
    dataset,
    id: propsId,
    variant,
    index = 0,
    isSelected: propsIsSelected,
    labelDecorator,
    legendDecorator,
    onClick,
    role,
    ...forwardedProps
  } = props;

  const theme = useTheme();

  const [_id] = useId();
  const id = propsId ?? _id;

  const isLegendCustom = variant === LegendVariant.custom && legendDecorator;
  const text = labelDecorator ? labelDecorator(data, { index, isSelected: propsIsSelected, color }) : data.data.key;
  const isEmpty = data.data.value === 0;

  const isSelectable = role === 'option';
  const isSelected = isSelectable && propsIsSelected === true;
  const isDeselected = isSelectable && propsIsSelected === false;

  const onKeyDown: KeyboardEventHandler<HTMLLIElement> = (event) => {
    if (onClick) {
      event.stopPropagation();

      if (event.code === 'Enter' || event.code === 'Space') {
        event.preventDefault();
        onClick(data, dataset);
      }
    }
  };

  return (
    <StyledLegendItem
      {...forwardedProps}
      className={classNames(LegendItem.className, className, `_${index}`, {
        selected: isSelected,
        deselected: isDeselected,
      })}
      $color={isDeselected ? 'var(--redsift-color-neutral-light-grey)' : color}
      ref={ref}
      aria-labelledby={!isEmpty ? `${id}-title` : undefined}
      aria-selected={isSelected ? true : isDeselected ? false : undefined}
      id={id}
      onClick={onClick ? () => onClick(data, dataset) : undefined}
      onKeyDown={onClick ? (event: React.KeyboardEvent<HTMLLIElement>) => onKeyDown(event) : undefined}
      role={role ? role : onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      $clickable={Boolean(onClick)}
      $variant={variant}
      $theme={theme}
    >
      {isLegendCustom ? (
        legendDecorator(data, { index, isSelected: propsIsSelected, color: props.color })
      ) : (
        <>
          <div />
          {typeof text === 'string' ? (
            variant === LegendVariant.value ? (
              <>
                <Number as="b" maximumFractionDigits={2} value={data.data.value} variant="inherit" />
                <Text variant="caption">{text}</Text>
              </>
            ) : variant === LegendVariant.percent && data.data.percent ? (
              <>
                <Number as="b" maximumFractionDigits={2} type="percent" value={data.data.percent} variant="inherit" />
                <Text variant="caption">{text}</Text>
              </>
            ) : (
              <Text variant="caption">{text}</Text>
            )
          ) : (
            <>{text}</>
          )}
        </>
      )}
    </StyledLegendItem>
  );
});
LegendItem.className = CLASSNAME;
LegendItem.displayName = COMPONENT_NAME;
